import { meldeExxasPause } from '../state/exxasPause.jsx';

export class ApiError extends Error {
  constructor(status, message, { timeout = false, code = null, retryAfter = null } = {}) {
    super(message);
    this.status = status;
    this.timeout = timeout;
    this.code = code;
    this.retryAfter = retryAfter; // Sekunden (bei 429: Exxas pausiert)
  }
}

/** Wird ausgelöst, wenn die API 401 meldet → die Anmeldung erscheint über der App (Eingaben bleiben erhalten). */
export const AUTH_EVENT = 'lexxa:anmelden';

/** Etwas über dem Exxas-Timeout der API (25 s), damit deren Fehlermeldung Vorrang hat. */
export const REQUEST_TIMEOUT_MS = 40_000;

/**
 * Fetch-Wrapper für /api/*. Auth läuft über das SWA-Cookie – kein Token im Frontend.
 * @param {string} path z. B. '/auftraege?q=…'
 * @param {{ method?: string, body?: unknown, timeoutMs?: number, idempotencyKey?: string }} [opts]
 */
export async function api(path, { method = 'GET', body, timeoutMs = REQUEST_TIMEOUT_MS, idempotencyKey } = {}) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  let res;
  try {
    res = await fetch(`/api${path}`, {
      method,
      credentials: 'same-origin',
      // CSRF-Schutz: die API verlangt diesen Header bei allen schreibenden Aufrufen
      headers: {
        'x-lexxa-csrf': '1',
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: ctrl.signal,
    });
  } catch {
    if (ctrl.signal.aborted) {
      // Bei Schreibzugriffen kann die Buchung trotzdem angekommen sein
      throw new ApiError(0, method === 'GET'
        ? 'Keine Antwort (Zeitüberschreitung). Bitte erneut versuchen.'
        : 'Keine Antwort (Zeitüberschreitung). Bitte prüfen, ob die Buchung angekommen ist.', { timeout: true });
    }
    throw new ApiError(0, 'Keine Verbindung. Bitte Netz prüfen.');
  } finally {
    clearTimeout(timer);
  }
  let data = null;
  try { data = await res.json(); } catch { data = null; }
  if (res.status === 401 && !path.startsWith('/auth/')) {
    window.dispatchEvent(new CustomEvent(AUTH_EVENT, { detail: { code: data?.code ?? null } }));
  }
  if (!res.ok) {
    const ra = Number.parseInt(res.headers?.get?.('retry-after') ?? '', 10);
    if (res.status === 429 && data?.code === 'EXXAS_AUSGELASTET') meldeExxasPause(Number.isFinite(ra) ? ra : 30);
    throw new ApiError(res.status, data?.error || 'Anfrage fehlgeschlagen', { timeout: res.status === 504, code: data?.code ?? null, retryAfter: Number.isFinite(ra) ? ra : null });
  }
  return data;
}

export const fileUrl = (fileId) => `/api/dateien/${encodeURIComponent(fileId)}`;

/**
 * Mehrere Zeiteinträge löschen (für «Rückgängig»).
 * @returns {Promise<string[]>} Fehlermeldungen der Einträge, die nicht gelöscht werden konnten
 */
export async function deleteLeistungen(nummer, ids) {
  const results = await Promise.allSettled(ids.map((id) => api(`/auftraege/${encodeURIComponent(nummer)}/leistungen/${encodeURIComponent(id)}`, { method: 'DELETE' })));
  return results.filter((r) => r.status === 'rejected').map((r) => r.reason?.message ?? 'Fehler');
}
